<template>
  <div class="mx-auto max-w-7xl">
    <nav
      class="mb-6 flex gap-2 "
      aria-label="Account Tabs"
    >
      <router-link
        v-for="tab in visibleTabs"
        :key="tab.to"
        :to="tab.to"
        class="whitespace-nowrap border-b-2 px-5 py-3 text-sm font-semibold transition"
        :class="
          isActive(tab)
            ? 'border-[#2D8CFF] text-[#2D8CFF]'
            : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700'
        "
      >
        {{ tab.label }}
      </router-link>
    </nav>

    <router-view />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { canAccessCreatorPath, fetchCreatorStaffSession } from '@/utils/creatorStaffAccess';

const route = useRoute();
const router = useRouter();
const canAccessGeneral = ref(true);
const canManageStaff = ref(true);
const canAccessBilling = ref(true);
const canViewUserSessions = ref(true);

const tabs = [
  { label: 'General', to: '/creator/account/general' },
  { label: 'Billing', to: '/creator/account/billing' },
  { label: 'Permissions', to: '/creator/account/user-permissions' },
  { label: 'Sessions', to: '/creator/account/user-sessions' },
  { label: 'Security', to: '/creator/account/security' },
];

const visibleTabs = computed(() => tabs.filter((tab) => {
  if (tab.to === '/creator/account/general') return canAccessGeneral.value;
  if (tab.to === '/creator/account/user-permissions') return canManageStaff.value;
  if (tab.to === '/creator/account/billing') return canAccessBilling.value;
  if (tab.to === '/creator/account/user-sessions') return canViewUserSessions.value;
  return true;
}));

const isActive = (tab) => String(route.path || '').startsWith(tab.to);

const loadStaffSession = async () => {
  try {
    const payload = await fetchCreatorStaffSession({ force: true });
    const isStaff = Boolean(payload?.isStaff);
    const role = String(payload?.role || '').toLowerCase();
    canAccessGeneral.value = !isStaff || role === 'admin' || canAccessCreatorPath(payload, '/creator/account/general');
    canManageStaff.value = !(isStaff && role !== 'admin');
    canAccessBilling.value = !isStaff || role === 'admin' || canAccessCreatorPath(payload, '/creator/account/billing');
    canViewUserSessions.value = !isStaff || role === 'admin';

    const currentPath = String(route.path || '');
    const blockedGeneral = !canAccessGeneral.value && currentPath.startsWith('/creator/account/general');
    const blockedUserPermissions = !canManageStaff.value && currentPath.startsWith('/creator/account/user-permissions');
    const blockedBilling = !canAccessBilling.value && currentPath.startsWith('/creator/account/billing');
    const blockedUserSessions = !canViewUserSessions.value && currentPath.startsWith('/creator/account/user-sessions');

    if (blockedGeneral || blockedUserPermissions || blockedBilling || blockedUserSessions) {
      router.replace('/creator/account/security');
    }
  } catch (_err) {
    // fail-open to avoid blocking account navigation
  }
};

onMounted(loadStaffSession);
</script>
